<script setup lang="ts">
import { FormSelect, FormInput, FormToggleSm } from "@easy/ui/forms";
import BlockImage from "../BlockImage.vue";
import {
    BlockDefinitionField,
    BlockDefinitionImageField,
    BlockDefinitionSelectField,
    BlockDefinitionSwitchField,
    BlockDefinitionTextField,
} from "@/apps/page/services/blocks/block.interfaces";
import { MediaType } from "@/views/blocks/PageBlock.interface";
import { computed } from "vue";
import { useI18n } from "vue-i18n";

const { fields, groupData } = defineProps<{
    fields: BlockDefinitionField[];
    groupData: any;
}>();

const { t } = useI18n();

const mediaTypeField = computed(
    () => fields.find((f) => f.value === "mediaType") as BlockDefinitionSelectField
);

const imageSrcField = computed(
    () =>
        fields.find((f) => f.value === "src" || f.value === "imageSrc") as BlockDefinitionImageField
);

const imageAltField = computed(
    () =>
        fields.find((f) => f.value === "alt" || f.value === "imageAlt") as BlockDefinitionTextField
);

const videoSrcField = computed(
    () => fields.find((f) => f.value === "videoSrc") as BlockDefinitionTextField
);

const videoAutoplayField = computed(
    () => fields.find((f) => f.value === "videoAutoplay") as BlockDefinitionSwitchField
);

const videoControlsField = computed(
    () => fields.find((f) => f.value === "videoControls") as BlockDefinitionSwitchField
);

const videoLoopField = computed(
    () => fields.find((f) => f.value === "videoLoop") as BlockDefinitionSwitchField
);

const iframeSrcField = computed(
    () => fields.find((f) => f.value === "iframeSrc") as BlockDefinitionTextField
);

const mediaTypeOptions = computed(() => [
    { label: t("page.block.fields.image"), value: MediaType.IMAGE },
    { label: t("page.block.fields.video"), value: MediaType.VIDEO },
    { label: t("page.block.fields.iframe"), value: MediaType.IFRAME },
]);
</script>

<template>
    <div class="space-y-3">
        <!-- Media Type Selector -->
        <FormSelect
            v-if="mediaTypeField"
            :id="mediaTypeField.value"
            :label="mediaTypeField.label"
            :options="mediaTypeOptions"
            v-model="groupData.mediaType"
            :value="groupData.mediaType || MediaType.IMAGE"
        />

        <template v-if="!groupData.mediaType || groupData.mediaType === MediaType.IMAGE">
            <BlockImage
                :id="imageSrcField?.value"
                :title="imageSrcField?.label"
                v-model="groupData[imageSrcField?.value]"
            />
            <FormInput
                :id="imageAltField?.value"
                :title="imageAltField?.label"
                v-model="groupData[imageAltField?.value]"
            />
        </template>

        <div class="space-y-3" v-if="groupData.mediaType === MediaType.VIDEO">
            <FormInput
                :id="videoSrcField?.value"
                :title="videoSrcField?.label"
                v-model="groupData[videoSrcField?.value]"
                placeholder="https://"
            />

            <div class="space-y-2">
                <FormToggleSm
                    :id="videoAutoplayField?.value"
                    v-model="groupData[videoAutoplayField?.value]"
                    :title="videoAutoplayField?.label"
                />

                <FormToggleSm
                    :id="videoControlsField?.value"
                    v-model="groupData[videoControlsField?.value]"
                    :title="videoControlsField?.label"
                />

                <FormToggleSm
                    :id="videoLoopField?.value"
                    v-model="groupData[videoLoopField?.value]"
                    :title="videoLoopField?.label"
                />
            </div>
        </div>

        <div class="space-y-3" v-if="groupData.mediaType === MediaType.IFRAME">
            <FormInput
                :id="iframeSrcField?.value"
                :title="iframeSrcField?.label"
                v-model="groupData[iframeSrcField?.value]"
                placeholder="https://www.youtube.com/embed/LxIR7eFw8HI?si=ObbVhSBAi35VMABl"
            />
        </div>
    </div>
</template>
